// The rule editor (LEAGUE_SETTINGS.md, scenario B3): every field a
// commissioner might touch, the section's plain-English sentences updated
// live, a preview of a scoring change on the last week with games in it,
// locks after the draft, and a versioned save with a reason.
//
// One screen for a practice league and a real one (`data/rules.ts`,
// PLAN-2026-09-26-every-screen-and-crayon P2). What a real league cannot
// change once it exists comes from the server as `fixed` — shown, disabled,
// with the reason beside it — so this screen holds no rule about it.
//
// The screen knows the settings schema, not the sport: stats, slots,
// positions, and presets all come from the SportDefinition. Drawn through the
// kit (LOOKS.md Part 7).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FORMAT_LABELS,
  applyRosterPreset,
  describeSettings,
  diffSettings,
  divisionSentences,
  followRoster,
  gameTimeLockSentences,
  playsTheField,
  scoringTable,
  startingPositions,
  validateLeagueSettings,
  waiverWeekdaysOf,
  type Issue,
  type LeagueRulesView,
  type LeagueSettings,
  type RulesTeam,
  type ScoringConfig,
} from '@fsp/domain';
import type { SportDefinition } from '@fsp/sports-core';
import { SEASON_AWARDS, WEEKLY_AWARDS } from '@fsp/awards';
import { Icon } from '../kit/Icon';
import {
  Button,
  Callout,
  Caption,
  Chips,
  Choice,
  Dialog,
  IconButton,
  Line,
  MultiChoice,
  NumberField,
  Stack,
  Text,
  TextArea,
  TextField,
  Title,
  Toggle,
  tell,
  type ChoiceOption,
} from '../kit/ui';
import { useLeagueRules, useRulesActions, useRulesPreview, type VoteOutcome } from '../data/rules';
import { useDelight } from '../delight/useDelight';
import { getSport } from '../sports/registry';
import { FormScreen, type FormSection } from '../templates/FormScreen';
import { FromLeague } from './shared/FromLeague';
import { PreviewTable } from './shared/RulesPreview';
import { RosterSlots } from './shared/RosterSlots';
import { ScoringTableView } from './shared/ScoringTableView';
import {
  ScoringDecimals,
  ScoringEditor,
  bonusGroups,
  type ScoringTarget,
} from './shared/ScoringEditor';
import { useLeagueSource } from '../data/leagueSource';
import { HALF_HOURS, isSlowClock, pickClockOptions } from './shared/clockTimes';
import { DivisionsSection } from './settings/DivisionsSection';
import { RivalsPlacement } from './settings/RivalsPlacement';

type SectionId =
  | 'league'
  | 'format'
  | 'scoring'
  | 'roster'
  | 'lineup'
  | 'schedule'
  | 'divisions'
  | 'tiebreakers'
  | 'waivers'
  | 'trades'
  | 'draft'
  | 'keepers'
  | 'governance'
  | 'fun'
  | 'tools';

/** The divisions' own fields, a section of their own inside the schedule's and the playoffs' rules. */
const DIVISION_PATHS = ['schedule.divisions', 'schedule.divisionGamesFirst', 'playoffs.seeding'];

const SECTIONS: {
  id: SectionId;
  title: string;
  paths: string[];
  /** Paths under `paths` another section owns, so an issue is counted once. */
  except?: string[];
  lockKeys: string[];
}[] = [
  { id: 'league', title: 'League', paths: ['identity'], lockKeys: [] },
  { id: 'format', title: 'Format', paths: ['format'], lockKeys: ['format'] },
  { id: 'scoring', title: 'Scoring', paths: ['scoring', 'categories'], lockKeys: ['scoring'] },
  { id: 'roster', title: 'Roster', paths: ['roster'], lockKeys: ['roster'] },
  { id: 'lineup', title: 'Lineups', paths: ['lineup'], lockKeys: ['lineup'] },
  {
    id: 'schedule',
    title: 'Schedule and playoffs',
    paths: ['schedule', 'playoffs'],
    except: DIVISION_PATHS,
    lockKeys: ['schedule', 'playoffs'],
  },
  {
    id: 'divisions',
    title: 'Divisions',
    paths: DIVISION_PATHS,
    lockKeys: ['schedule', 'playoffs'],
  },
  { id: 'tiebreakers', title: 'Tiebreakers', paths: ['tiebreakers'], lockKeys: [] },
  {
    id: 'waivers',
    title: 'Waivers and free agents',
    paths: ['waivers', 'acquisitions'],
    lockKeys: ['waivers'],
  },
  { id: 'trades', title: 'Trades', paths: ['trades'], lockKeys: ['trades'] },
  { id: 'draft', title: 'Draft', paths: ['draft'], lockKeys: [] },
  { id: 'keepers', title: 'Keepers', paths: ['keepers'], lockKeys: ['keepers'] },
  { id: 'governance', title: 'Rule changes', paths: ['governance'], lockKeys: [] },
  { id: 'fun', title: 'Fun and awards', paths: ['fun'], lockKeys: [] },
  { id: 'tools', title: 'Helper tools', paths: ['tools'], lockKeys: [] },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TIEBREAKERS: { value: LeagueSettings['tiebreakers']['standings'][number]; label: string }[] =
  [
    { value: 'head-to-head', label: 'Head-to-head record' },
    { value: 'points-for', label: 'Points scored' },
    { value: 'points-against', label: 'Points allowed' },
    { value: 'division-record', label: 'Division record' },
    { value: 'record-vs-common', label: 'Record vs common opponents' },
    { value: 'all-play-record', label: 'All-play record' },
    { value: 'points-for-last-3', label: 'Points over the last 3 weeks' },
    { value: 'coin-flip', label: 'Coin flip' },
  ];

const LOCKABLE = [
  'scoring',
  'roster',
  'lineup',
  'format',
  'schedule',
  'playoffs',
  'waivers',
  'trades',
  'keepers',
] as const;

function zones(): string[] {
  const common = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Europe/London',
  ];
  try {
    const all = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf?.('timeZone');
    return all ? [...new Set([...common, ...all])] : common;
  } catch {
    return common;
  }
}

function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const under = (path: string, root: string): boolean =>
  path === root || path.startsWith(`${root}.`) || path.startsWith(`${root}[`);

/** Whether an issue at `path` belongs to a section: under its paths and not under another's. */
const owns = (section: { paths: string[]; except?: string[] }, path: string): boolean =>
  section.paths.some((p) => path.startsWith(p)) &&
  !(section.except ?? []).some((p) => path.startsWith(p));

/** The unsaved draft kept for this tab, if it was begun from this version (B-418). */
function keptDraft(key: string, version: number): LeagueSettings | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const kept = JSON.parse(raw) as { version: number; settings: LeagueSettings };
    return kept.version === version ? kept.settings : null;
  } catch {
    return null;
  }
}

/** Keeps the draft for this tab, or forgets it with nothing unsaved. */
function keepDraft(key: string, kept: { version: number; settings: LeagueSettings } | null): void {
  try {
    if (kept === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(kept));
  } catch {
    // A tab that cannot keep it still edits; only leaving loses it.
  }
}

/**
 * League settings, or with `tool="scoring"` the commissioner's scoring tool
 * (PLAN-2026-10-18 §2.5): the same draft, save, reason, unlock and vote, with
 * only the scoring editor on screen. Everybody's scoring is the table to read
 * in Settings and on the rules page; changing it is this, opened from
 * Settings, and after the draft it is meant to be rare.
 */
export function SettingsScreen({ tool }: { tool?: 'scoring' } = {}) {
  return (
    <FromLeague snapshot={useLeagueRules()}>
      {(rules) => <SettingsEditor rules={rules} tool={tool} />}
    </FromLeague>
  );
}

function SettingsEditor({ rules, tool }: { rules: LeagueRulesView; tool?: 'scoring' }) {
  const { play } = useDelight();
  const navigate = useNavigate();
  const actions = useRulesActions();
  const sport = getSport(rules.league.sport as never);
  // A non-commissioner reads every field and changes none.
  const canEdit = rules.canEdit;
  const drafted = rules.drafted;

  // THE VERSION THIS EDIT STARTED FROM. The rules are read again after any
  // save in the league, so a colleague's save arrives here as a newer
  // `rules`; the edit stays on screen and says so rather than vanishing
  // (P2's review), and Save waits until the commissioner starts again from
  // the new version — saving the old draft over it would undo their change.
  const [base, setBase] = useState<LeagueRulesView>(rules);
  const overtaken = rules.version !== base.version;
  // AN EDIT IS NOT LOST BY LEAVING (B-418): the unsaved draft is kept for
  // this tab, by league and the version it started from, so a press on
  // another screen and Back finds it as it was; a newer version since, and
  // it is the newer version that opens.
  const draftKey = `settings-draft:${rules.league.id}`;
  // Only for somebody who can still make it (the review of B-418): a
  // commissioner who handed the league on reads the rules as they are.
  const [draft, setDraft] = useState<LeagueSettings>(() => {
    const kept = canEdit ? keptDraft(draftKey, rules.version) : null;
    return kept ?? structuredClone(rules.settings);
  });
  // A SECTION NAMED IN THE ADDRESS opens first: the pre-draft screen's
  // *Change the draft time* lands on Draft (`?section=draft`).
  const [params] = useSearchParams();
  const [active, setActive] = useState<SectionId>(() => {
    const named = params.get('section');
    return SECTIONS.some((x) => x.id === named) ? (named as SectionId) : 'scoring';
  });
  // OPENING THE TOOL IS THE UNLOCK: past the draft the scoring section is
  // locked, and only the tool can change it, so a scoring change in the draft
  // was made there on purpose.
  const [unlocked, setUnlocked] = useState<Set<string>>(new Set(['scoring']));
  // After the draft the tool says what a change means before anything can
  // be touched; before it, finishing the setup is ordinary.
  const [understood, setUnderstood] = useState(!rules.drafted);
  const [scoringTarget, setScoringTarget] = useState<ScoringTarget>(null);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // What the server said on the last refused save, until the next edit.
  const [refused, setRefused] = useState<Issue[]>([]);

  // Editing any scoring or roster field turns the preset into "Custom, based on"
  // (RULES_SCHEMA.md §20); picking a preset goes through applyScoringPreset instead.
  // READ THE EVENT, THEN CALL `set` (B-111): every control hands its value in.
  const set = (mutate: (s: LeagueSettings) => void) => {
    setRefused([]);
    setDraft((d) => {
      const next = structuredClone(d);
      mutate(next);
      const { presetId: _s, ...scoringBefore } = d.scoring;
      const { presetId: _s2, ...scoringAfter } = next.scoring;
      if (JSON.stringify(scoringBefore) !== JSON.stringify(scoringAfter))
        next.scoring.presetId = null;
      const { presetId: _r, ...rosterBefore } = d.roster;
      const { presetId: _r2, ...rosterAfter } = next.roster;
      if (JSON.stringify(rosterBefore) !== JSON.stringify(rosterAfter)) next.roster.presetId = null;
      // A round per roster spot (§4.3 of PLAN-2026-10-18-families-and-newcomers).
      return followRoster(d, next);
    });
  };

  const changedPaths = useMemo(
    () => diffSettings(base.settings, draft).map((d) => d.path),
    [base.settings, draft],
  );
  const issues = useMemo(() => {
    // A SEASON STARTED FROM THE LAST may draft fewer rounds than its roster
    // when its dynasty carried every roster (§2.4 rule 2).
    const own = validateLeagueSettings(draft, sport, base.settings, {
      carried: rules.league.lastSeasonId !== null,
    });
    // The fixed rules, from the server's list: a change under one is refused
    // there, so it is said here before anybody presses Save.
    for (const f of rules.fixed) {
      if (changedPaths.some((p) => under(p, f.path) || under(f.path, p))) {
        own.push({ path: f.path, severity: 'error', message: f.why });
      }
    }
    return [...own, ...refused];
  }, [draft, sport, rules.fixed, rules.league.lastSeasonId, changedPaths, refused]);
  const doc = useMemo(() => describeSettings(draft, sport), [draft, sport]);
  const dirtyTops = useMemo(
    () => [...new Set(changedPaths.map((p) => p.split(/[.[]/)[0]!))],
    [changedPaths],
  );
  const dirty = changedPaths.length > 0;
  useEffect(() => {
    keepDraft(draftKey, dirty && canEdit ? { version: base.version, settings: draft } : null);
  }, [draftKey, dirty, canEdit, base.version, draft]);
  // A RELOAD OR A CLOSED TAB is the one leaving that loses it: the browser's
  // own question, while there is anything to lose.
  useEffect(() => {
    if (!dirty || !canEdit) return;
    const ask = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirty, canEdit]);
  const lockedKeys: readonly string[] = rules.locked;

  /** Fixed in a league that exists: the path itself or anything under it. */
  const isFixed = (path: string) => rules.fixed.some((f) => under(path, f.path));
  const fixedWhy = (path: string) => rules.fixed.find((f) => under(path, f.path))?.why ?? null;

  // THE SCORING AS A TABLE (§2.2): to read in Settings, the editor in the tool.
  const scoringNow = useMemo(
    () =>
      scoringTable(draft.scoring, sport, {
        positions: startingPositions(draft.roster, sport),
        editing: tool === 'scoring',
      }),
    [draft.scoring, draft.roster, sport, tool],
  );
  const setScoring = (next: ScoringConfig) =>
    set((s) => {
      s.scoring = next;
    });
  const scoringTool = `/l/${rules.league.id}/settings/scoring`;

  const sections: FormSection[] = SECTIONS.filter((s) => tool === undefined || s.id === tool).map(
    (s) => ({
      id: s.id,
      title: s.title,
      errors: issues.filter((i) => i.severity === 'error' && owns(s, i.path)).length,
      warnings: issues.filter((i) => i.severity === 'warning' && owns(s, i.path)).length,
      // Scoring in Settings is a table to read, not a locked form (§2.5).
      locked:
        s.id !== 'scoring' &&
        s.lockKeys.some((k) => lockedKeys.includes(k)) &&
        !s.lockKeys.every((k) => unlocked.has(k)),
      fixed: s.paths.every((p) => isFixed(p)),
    }),
  );
  const current = SECTIONS.find((s) => s.id === active)!;
  const shown = sections.find((s) => s.id === active)!;
  const sectionLocked = shown.locked ?? false;
  const sectionFixed = shown.fixed ?? false;
  const disabled = !canEdit || sectionLocked || sectionFixed;
  const sectionIssues = issues.filter((i) => owns(current, i.path));
  const divided = divisionSentences(draft);
  const sentences =
    active === 'divisions'
      ? [divided.schedule, divided.playoffs].filter((x): x is string => x !== null)
      : (doc.sections.find((s) => s.id === active)?.sentences ?? []);
  const extraSentences =
    active === 'schedule' ? (doc.sections.find((s) => s.id === 'playoffs')?.sentences ?? []) : [];

  const previewable = ['scoring', 'roster'].some((k) => dirtyTops.includes(k));
  const preview = useRulesPreview(actions, draft, previewable);
  const unlockNeeded = lockedKeys.some((k) => dirtyTops.includes(k));
  const errorCount = issues.filter((i) => i.severity === 'error').length;

  // IN A LEAGUE THAT VOTES, past the draft, a change is put to the managers
  // rather than saved (LEAGUE_SETTINGS.md "Votes", B6), and while one is up
  // for a vote no other can be.
  const toVote = rules.changesGoToVote;
  const voting = rules.proposal !== null;
  const share =
    rules.settings.governance.changesRequire === 'supermajority-vote'
      ? `${Math.round(rules.settings.governance.supermajorityFraction * 100)}% of the managers`
      : 'a majority of the managers';

  const save = async () => {
    setSaving(true);
    const input = { settings: draft, reason, unlock: unlockNeeded, basedOn: base.version };
    const out = toVote ? await actions.propose(input) : await actions.save(input);
    setSaving(false);
    if (!out.ok) {
      setRefused(out.issues);
      tell(out.message, 'bad');
      return;
    }
    setReasonOpen(false);
    // Saved, or put to the vote: nothing unsaved is left to keep.
    keepDraft(draftKey, null);
    if (toVote) {
      tell('Put to the league’s vote. Every manager can vote on the rules page.', 'good');
      navigate(`/l/${rules.league.id}/rules`);
      return;
    }
    play('win');
    tell(
      `Saved as version ${out.version}.${
        out.appliesFrom && dirtyTops.includes('scoring')
          ? ` The scoring change counts from ${out.appliesFrom}.`
          : ''
      }`,
      'good',
    );
    navigate(`/l/${rules.league.id}/rules`);
  };

  // No preset chips for scoring (§2.5): its presets have no names on screen.
  const strip =
    active === 'roster' ? (
      <PresetStrip
        current={draft.roster.presetId}
        presets={sport.rosterPresets}
        disabled={disabled}
        onPick={(id) => setDraft((d) => followRoster(d, applyRosterPreset(d, sport, id)))}
      />
    ) : null;

  const scoringContent =
    tool === 'scoring' ? (
      <Stack gap={3}>
        {drafted ? (
          <Callout tone="warn">
            <Stack gap={2}>
              <span>
                {`Changing scoring now counts from ${rules.appliesFrom?.label ?? 'the next week'}. Nothing already played changes. Everyone sees the change and your reason.`}
                {toVote
                  ? ` The league votes on it: it changes the scoring once ${share} say yes.`
                  : ''}
              </span>
              {understood ? null : (
                <div>
                  <Button size="xs" icon="edit" onClick={() => setUnderstood(true)}>
                    Change scoring
                  </Button>
                </div>
              )}
            </Stack>
          </Callout>
        ) : null}
        {understood ? (
          <>
            <Text size="sm" tone="secondary" block>
              {scoringNow.heading}. Tap a number to change it.
            </Text>
            <ScoringTableView
              table={scoringNow}
              editing={canEdit}
              onPick={(row, s) => setScoringTarget({ kind: 'row', row, section: s })}
              canAddBonus={bonusGroups(sport)}
              onAddBonus={(s, group) => setScoringTarget({ kind: 'add-bonus', section: s, group })}
            />
            <ScoringDecimals scoring={draft.scoring} sport={sport} onChange={setScoring} />
            <ScoringEditor
              target={scoringTarget}
              onClose={() => setScoringTarget(null)}
              sport={sport}
              scoring={draft.scoring}
              table={scoringNow}
              onChange={setScoring}
            />
          </>
        ) : null}
      </Stack>
    ) : (
      <Stack gap={3}>
        <Line gap={2} spread wrap>
          <Text size="sm" tone="secondary">
            {scoringNow.heading}.
          </Text>
          {canEdit && !drafted ? (
            <Button size="xs" icon="edit" to={scoringTool}>
              Edit scoring
            </Button>
          ) : null}
        </Line>
        <ScoringTableView table={scoringNow} searchable />
        {/* KEPT OUT OF THE WAY (§2.5): a change mid-season should be rare. */}
        {canEdit && drafted ? (
          <div>
            <Button size="xs" variant="quiet" to={scoringTool}>
              Change scoring after the draft…
            </Button>
          </div>
        ) : null}
      </Stack>
    );

  // WHAT CAN STILL CHANGE, said once at the top (DIRECTION A17, B-431).
  const stageLine =
    !canEdit || tool === 'scoring'
      ? null
      : rules.stage === 'before-draft'
        ? 'Before the draft, nearly everything here can change: the draft time and how it runs, the roster, the format, waivers. The number of teams and an auction were set when the league was made. From the draft on, the roster, the draft and waivers hold for the season.'
        : rules.stage === 'before-season'
          ? 'The schedule and the playoffs can change until the season opens. What the draft was built on holds for the season.'
          : null;

  const content = (
    <>
      {stageLine !== null ? (
        <Text size="sm" tone="secondary" block>
          {stageLine}
        </Text>
      ) : null}
      {voting && canEdit ? (
        <Callout tone="warn">
          <Line gap={2} spread wrap>
            <span>
              A rule change is up for the league’s vote. Another can be put to it once that one is
              decided or withdrawn.
            </span>
            <Button size="xs" onClick={() => navigate(`/l/${rules.league.id}/rules`)}>
              See the vote
            </Button>
          </Line>
        </Callout>
      ) : null}
      {overtaken ? (
        <Callout tone="warn">
          <Stack gap={2}>
            <span>
              Somebody saved version {rules.version} while you were editing. Saving this would undo
              their change, so it waits: read what changed on the rules page, then start again from
              version {rules.version}.
            </span>
            <div>
              <Button
                size="xs"
                onClick={() => {
                  setBase(rules);
                  setDraft(structuredClone(rules.settings));
                  setUnlocked(new Set());
                  setRefused([]);
                }}
              >
                Start from version {rules.version}
              </Button>
            </div>
          </Stack>
        </Callout>
      ) : null}
      {!canEdit ? (
        <Callout>
          <Line gap={2}>
            <Icon name="locked" size="sm" />
            <span>
              Only {rules.league.commissionerName} can change these. You can read every rule on the
              rules page.
            </span>
          </Line>
        </Callout>
      ) : sectionFixed ? (
        <Callout>
          <Line gap={2} align="start">
            <Icon name="locked" size="sm" />
            <span>{fixedWhy(current.paths[0]!) ?? 'Set when the league was made.'}</span>
          </Line>
        </Callout>
      ) : sectionLocked ? (
        <Callout>
          <Line gap={2} spread wrap>
            <Line gap={2}>
              <Icon name="locked" size="sm" />
              <span>Locked after the draft so nobody changes the game mid-season.</span>
            </Line>
            <Button
              size="xs"
              icon="unlock"
              onClick={() => setUnlocked((u) => new Set([...u, ...current.lockKeys]))}
            >
              Unlock for this edit
            </Button>
          </Line>
        </Callout>
      ) : null}
      {active === 'scoring' ? (
        scoringContent
      ) : active === 'divisions' ? (
        <DivisionsSection
          draft={draft}
          saved={rules.settings}
          set={set}
          disabled={disabled}
          teams={rules.teams}
          // WHERE A TEAM PLAYS changes until the season opens, by a
          // commissioner, in a league whose teams the server listed.
          canPlace={canEdit && rules.teams.length > 0 && !isFixed('schedule.divisions')}
          onPlace={actions.placeTeam}
          voting={rules.proposal?.open ? rules.proposal.settings.schedule.divisions : null}
        />
      ) : (
        <SectionFields
          id={active}
          draft={draft}
          set={set}
          sport={sport}
          disabled={disabled}
          isFixed={(path) => !canEdit || isFixed(path)}
          teamCount={rules.league.teamCount}
          hasLastSeason={rules.league.lastSeasonId !== null}
          savedWaiverDay={
            base.settings.waivers.processing.schedule === 'weekly'
              ? base.settings.waivers.processing.weekday
              : null
          }
          rivals={{
            teams: rules.teams,
            // A RIVAL is named until the season opens, by a commissioner, in a
            // league whose teams the server listed.
            canName: canEdit && rules.teams.length > 0 && !isFixed('schedule.rivalryPeriods'),
            onName: actions.placeRival,
          }}
        />
      )}
      {sectionIssues.length ? (
        <Stack gap={1}>
          {sectionIssues.map((i, k) => (
            <Text key={k} tone={i.severity === 'error' ? 'bad' : 'warn'} block>
              {i.message}
            </Text>
          ))}
        </Stack>
      ) : null}
      {/* The scoring's own words are its table. */}
      {active === 'scoring' ? null : (
        <div className="fsp-form-sentences">
          {[...sentences, ...extraSentences].map((line, i) => (
            <span key={i}>{line}</span>
          ))}
        </div>
      )}
    </>
  );

  const previewNode = previewable ? (
    <PreviewTable preview={preview.preview} failure={preview.failure} pending={preview.pending} />
  ) : undefined;

  return (
    <>
      <FormScreen
        header={
          <Stack gap={0}>
            <Title>{tool === 'scoring' ? 'Change scoring' : 'League settings'}</Title>
            <Text tone="secondary" size="xs">
              {doc.headline} · {rules.league.name}
              {drafted ? ' · drafted' : ' · not drafted yet'}
            </Text>
          </Stack>
        }
        sections={sections}
        active={active}
        onSection={(id) => setActive(id as SectionId)}
        strip={strip}
        content={content}
        preview={previewNode}
        previewLabel="Preview"
        dirtyLabel={
          dirty
            ? `${dirtyTops.length} ${dirtyTops.length === 1 ? 'part' : 'parts'} changed${
                errorCount ? ` · ${errorCount} ${errorCount === 1 ? 'problem' : 'problems'}` : ''
              }`
            : null
        }
        onDiscard={() => {
          setBase(rules);
          setDraft(structuredClone(rules.settings));
          setUnlocked(new Set());
          setRefused([]);
        }}
        onSave={() => (drafted ? setReasonOpen(true) : void save())}
        saveDisabled={
          saving ||
          overtaken ||
          !canEdit ||
          (toVote && voting) ||
          errorCount > 0 ||
          (unlockNeeded && !lockedKeys.every((k) => !dirtyTops.includes(k) || unlocked.has(k)))
        }
        // "Propose", the spec's word, and short: at 390 the save bar keeps
        // room for the chat chip, and "Put it to a vote" broke the dirty count
        // mid-word. The reason dialog says what proposing does.
        saveLabel={
          toVote
            ? unlockNeeded
              ? 'Unlock and propose'
              : 'Propose'
            : unlockNeeded
              ? 'Save and unlock'
              : 'Save'
        }
      />
      <Dialog opened={reasonOpen} onClose={() => setReasonOpen(false)} title="Why the change?">
        <Stack gap={3}>
          <Text tone="secondary" block>
            {toVote
              ? `The league votes on it: it changes the rules once ${share} say yes, and it fails if they have not within three days. Everyone sees it on the rules page and in the league’s chat.`
              : 'Everyone will see this on the rules page and in the league’s chat.'}
            {rules.appliesFrom && dirtyTops.includes('scoring')
              ? toVote
                ? ' A scoring change counts from the first week not yet started when it passes; nothing already played changes.'
                : ` The scoring change counts from ${rules.appliesFrom.label}; nothing already played changes.`
              : ''}
          </Text>
          <TextArea
            label="Reason"
            value={reason}
            onChange={setReason}
            placeholder="Bumped TE receptions so Dave stops complaining."
            maxLength={500}
          />
          <Line gap={2} spread>
            <Button variant="quiet" onClick={() => setReasonOpen(false)}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={() => void save()}
              disabled={saving || !reason.trim()}
            >
              {toVote ? 'Put it to a vote' : 'Save version'}
            </Button>
          </Line>
        </Stack>
      </Dialog>
    </>
  );
}

function PresetStrip({
  current,
  presets,
  disabled,
  onPick,
}: {
  current: string | null;
  presets: readonly { id: string; name: string; description?: string }[];
  disabled: boolean;
  onPick: (id: string) => void;
}) {
  return (
    <Line gap={2} wrap>
      <Caption>Start from a preset</Caption>
      <Chips
        label="Presets"
        single
        options={presets.map((p) => ({ value: p.id, label: p.name, hint: p.description }))}
        value={current === null ? [] : [current]}
        disabled={disabled}
        onChange={(v) => (v[0] ? onPick(v[0]) : undefined)}
      />
      {current === null ? <Text tone="secondary">Custom</Text> : null}
    </Line>
  );
}

/** One section's controls. Every control mutates a clone of the draft. */
function SectionFields({
  id,
  draft: d,
  set,
  sport,
  disabled,
  isFixed,
  teamCount,
  savedWaiverDay,
  hasLastSeason,
  rivals,
}: {
  id: SectionId;
  draft: LeagueSettings;
  set: (mutate: (s: LeagueSettings) => void) => void;
  sport: SportDefinition;
  disabled: boolean;
  /** A field inside an editable section that is still fixed (the time zone). */
  isFixed: (path: string) => boolean;
  teamCount: number;
  /** The weekly run's day as the league has it saved, which it may keep (B-509). */
  savedWaiverDay: number | null;
  /** The league has a season before this one, whose finish a draft order can reverse. */
  hasLastSeason: boolean;
  /** Each team's rival, named here while the draft has rivalry weeks. */
  rivals: {
    teams: readonly RulesTeam[];
    canName: boolean;
    onName: (teamId: string, rivalTeamId: string | null) => Promise<VoteOutcome>;
  };
}) {
  const source = useLeagueSource();
  const periodOptions: ChoiceOption[] = Array.from({ length: 22 }, (_, i) => ({
    value: String(i + 1),
    label: `Week ${i + 1}`,
  }));

  switch (id) {
    case 'league':
      return (
        <div className="fsp-form-fields">
          <TextField
            label="League name"
            value={d.identity.name}
            onChange={(v) => set((s) => (s.identity.name = v))}
            maxLength={60}
            disabled={disabled}
          />
          <TextArea
            label="Description"
            value={d.identity.description}
            onChange={(v) => set((s) => (s.identity.description = v))}
            maxLength={500}
            disabled={disabled}
          />
          <TextField
            label="Trophy name"
            value={d.identity.branding.trophyName}
            onChange={(v) => set((s) => (s.identity.branding.trophyName = v))}
            maxLength={40}
            disabled={disabled}
          />
          {/* TODO(B11): the logo and banner — nothing stores an image yet, and
              where they show needs a design — and the accent color's control. */}
          <Choice
            label="Time zone"
            description={
              isFixed('identity.timeZone')
                ? 'Every rule time was set in this zone when the league was made.'
                : 'Lock times, waiver processing, and reminders use it.'
            }
            value={d.identity.timeZone}
            onChange={(v) => v && set((s) => (s.identity.timeZone = v))}
            options={zones().map((z) => ({ value: z, label: z }))}
            required
            disabled={disabled || isFixed('identity.timeZone')}
          />
        </div>
      );
    case 'format':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="Format"
            searchable={false}
            required
            value={d.format.id}
            onChange={(v) =>
              v &&
              set((s) => {
                s.format.id = v as LeagueSettings['format']['id'];
                s.format.elimination =
                  v === 'elimination'
                    ? (s.format.elimination ?? {
                        eliminatePerPeriod: 1,
                        eliminatedRosterTo: 'waivers',
                        stopWhenTeamsRemain: 1,
                      })
                    : null;
                s.lineup.bestBall = v === 'best-ball';
              })
            }
            options={sport.supportedFormats.map((f) => ({ value: f, label: FORMAT_LABELS[f] }))}
            disabled={disabled}
          />
          <Toggle
            label="Median game: everyone also plays the league median each week"
            on={d.format.h2h.medianGame}
            onChange={(on) => set((s) => (s.format.h2h.medianGame = on))}
            disabled={disabled}
          />
          {/* HEAD TO HEAD ONLY (Q6): a league against the field has nobody to play twice. */}
          {playsTheField(d.format.id) ? null : (
            <Toggle
              label="Two opponents each week"
              description="Your one score against each of two teams every regular-season week, a result in each game."
              on={d.format.h2h.twoOpponents}
              onChange={(on) => set((s) => (s.format.h2h.twoOpponents = on))}
              disabled={disabled}
            />
          )}
          {d.format.elimination ? (
            <NumberField
              label="Teams eliminated per week"
              value={d.format.elimination.eliminatePerPeriod}
              onChange={(v) => set((s) => (s.format.elimination!.eliminatePerPeriod = v ?? 1))}
              min={1}
              disabled={disabled}
            />
          ) : null}
        </div>
      );
    case 'scoring':
      // THE SCORING IS A TABLE (PLAN-2026-10-18 §2.5), drawn by the screen
      // above rather than as fields here: the raw rule rows are gone.
      return null;
    case 'roster': {
      const reserve = sport.slots.find((sl) => sl.kind === 'reserve');
      const taxiSlot = sport.slots.find((sl) => sl.kind === 'taxi');
      return (
        <RosterSlots
          roster={{ slots: d.roster.slots, reserve: d.roster.reserve?.count ?? 0 }}
          sport={sport}
          disabled={disabled}
          taxi={{
            rule: d.roster.taxi,
            weeks: {
              first: d.schedule.regularSeason.firstPeriod,
              last: d.schedule.regularSeason.lastPeriod,
            },
            onChange: (next) =>
              set((s) => {
                s.roster.taxi =
                  next === null || taxiSlot === undefined
                    ? null
                    : { slot: s.roster.taxi?.slot ?? taxiSlot.id, ...next };
              }),
          }}
          onSlot={(slot, count) =>
            set((s) => {
              const r = s.roster.slots.find((x) => x.slot === slot);
              if (r) r.count = count;
              else s.roster.slots.push({ slot, count });
            })
          }
          onReserve={(count) =>
            set((s) => {
              if (count === 0 || reserve === undefined) s.roster.reserve = null;
              else
                s.roster.reserve = s.roster.reserve
                  ? { ...s.roster.reserve, count }
                  : {
                      slot: reserve.id,
                      count,
                      eligibleStatuses: sport.injuryStatuses
                        .filter((st) => st.severity === 'out')
                        .map((st) => st.id),
                      mustMoveWhenHealthy: true,
                    };
            })
          }
        />
      );
    }
    case 'lineup':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="Set lineups"
            searchable={false}
            required
            value={d.lineup.period}
            onChange={(v) => v && set((s) => (s.lineup.period = v as 'weekly' | 'daily'))}
            options={sport.lineupPeriods.map((p) => ({
              value: p,
              label: p === 'weekly' ? 'Weekly' : 'Daily',
            }))}
            disabled={disabled || sport.lineupPeriods.length === 1}
          />
          <Choice
            label="Players lock"
            searchable={false}
            required
            value={d.lineup.lockRule}
            onChange={(v) =>
              v && set((s) => (s.lineup.lockRule = v as LeagueSettings['lineup']['lockRule']))
            }
            options={[
              { value: 'per-player-game', label: 'When their own game starts' },
              { value: 'first-game', label: "All at the week's first game" },
              { value: 'fixed-time', label: 'At a fixed time' },
            ]}
            disabled={disabled}
          />
          {d.lineup.lockRule === 'fixed-time' ? (
            <TextField
              type="time"
              label="Lock time"
              value={d.lineup.fixedLockTime ?? '13:00'}
              onChange={(v) => set((s) => (s.lineup.fixedLockTime = v))}
              disabled={disabled}
            />
          ) : null}
          <Toggle
            label="Best ball: the best lineup is picked automatically after the games"
            on={d.lineup.bestBall}
            onChange={(on) => set((s) => (s.lineup.bestBall = on))}
            disabled={disabled}
          />
          <Toggle
            label="Allow empty starting slots"
            on={d.lineup.allowEmptySlots}
            onChange={(on) => set((s) => (s.lineup.allowEmptySlots = on))}
            disabled={disabled}
          />
        </div>
      );
    case 'schedule':
      return (
        <div className="fsp-form-fields">
          <div className="fsp-form-pair">
            <NumberField
              label="Regular season starts"
              value={d.schedule.regularSeason.firstPeriod}
              onChange={(v) => set((s) => (s.schedule.regularSeason.firstPeriod = v ?? 1))}
              min={1}
              max={22}
              disabled={disabled}
            />
            <NumberField
              label="Regular season ends"
              value={d.schedule.regularSeason.lastPeriod}
              onChange={(v) => set((s) => (s.schedule.regularSeason.lastPeriod = v ?? 14))}
              min={1}
              max={22}
              disabled={disabled}
            />
          </div>
          <MultiChoice
            label="Rivalry weeks"
            value={d.schedule.rivalryPeriods.map(String)}
            onChange={(v) =>
              set((s) => (s.schedule.rivalryPeriods = v.map(Number).sort((a, b) => a - b)))
            }
            options={periodOptions}
            disabled={disabled}
          />
          {d.schedule.rivalryPeriods.length > 0 && rivals.teams.length > 0 ? (
            <RivalsPlacement teams={rivals.teams} canName={rivals.canName} onName={rivals.onName} />
          ) : null}
          <Caption>Playoffs</Caption>
          <div className="fsp-form-pair">
            <NumberField
              label="Teams in the playoffs"
              value={d.playoffs.teamCount}
              onChange={(v) => set((s) => (s.playoffs.teamCount = v ?? 0))}
              min={0}
              max={Math.min(16, teamCount)}
              disabled={disabled}
            />
            <NumberField
              label="Byes"
              value={d.playoffs.byes}
              onChange={(v) => set((s) => (s.playoffs.byes = v ?? 0))}
              min={0}
              max={8}
              disabled={disabled}
            />
          </div>
          <MultiChoice
            label="Playoff weeks"
            value={d.playoffs.periods.map(String)}
            onChange={(v) => set((s) => (s.playoffs.periods = v.map(Number).sort((a, b) => a - b)))}
            options={periodOptions}
            disabled={disabled}
          />
          <Choice
            label="Championship"
            searchable={false}
            required
            value={String(d.playoffs.championshipPeriods)}
            onChange={(v) => v && set((s) => (s.playoffs.championshipPeriods = Number(v)))}
            options={[
              { value: '1', label: 'One week' },
              { value: '2', label: 'Two weeks' },
            ]}
            disabled={disabled}
          />
          <Choice
            label="The rest"
            searchable={false}
            required
            value={d.playoffs.consolation}
            onChange={(v) =>
              v &&
              set((s) => (s.playoffs.consolation = v as LeagueSettings['playoffs']['consolation']))
            }
            options={[
              { value: 'none', label: 'Season over' },
              { value: 'bracket', label: 'Consolation bracket' },
              { value: 'toilet-bowl', label: 'Toilet bowl' },
            ]}
            disabled={disabled}
          />
          <Toggle
            label="Reseed each round"
            on={d.playoffs.reseed}
            onChange={(on) => set((s) => (s.playoffs.reseed = on))}
            disabled={disabled}
          />
        </div>
      );
    case 'tiebreakers':
      return (
        <div className="fsp-form-fields">
          <Caption>Standings ties, in order</Caption>
          {d.tiebreakers.standings.map((t, i) => (
            <div key={`${t}-${i}`} className="fsp-form-order">
              <Text tone="muted" numeric>
                {i + 1}
              </Text>
              <Text>{TIEBREAKERS.find((x) => x.value === t)?.label ?? t}</Text>
              <IconButton
                icon="up"
                label="Move up"
                variant="quiet"
                disabled={disabled || i === 0}
                onClick={() => set((s) => swap(s.tiebreakers.standings, i, i - 1))}
              />
              <IconButton
                icon="down"
                label="Move down"
                variant="quiet"
                disabled={disabled || i === d.tiebreakers.standings.length - 1}
                onClick={() => set((s) => swap(s.tiebreakers.standings, i, i + 1))}
              />
              <IconButton
                icon="delete"
                label="Remove"
                variant="quiet"
                disabled={disabled || d.tiebreakers.standings.length === 1}
                onClick={() => set((s) => void s.tiebreakers.standings.splice(i, 1))}
              />
            </div>
          ))}
          <Choice
            label="Add a tiebreaker"
            hideLabel
            placeholder="Add a tiebreaker"
            searchable={false}
            value={null}
            onChange={(v) => v && set((s) => s.tiebreakers.standings.push(v as never))}
            options={TIEBREAKERS.filter((t) => !d.tiebreakers.standings.includes(t.value))}
            disabled={disabled}
          />
          <Choice
            label="A tied matchup"
            searchable={false}
            required
            value={d.tiebreakers.matchup}
            onChange={(v) =>
              v &&
              set((s) => (s.tiebreakers.matchup = v as LeagueSettings['tiebreakers']['matchup']))
            }
            options={[
              { value: 'tie', label: 'Is a tie' },
              { value: 'bench-points', label: 'Goes to bench points' },
              { value: 'home-team', label: 'Goes to the home team' },
              { value: 'coin-flip', label: 'Goes to a coin flip' },
            ]}
            disabled={disabled}
          />
        </div>
      );
    case 'waivers':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="Waiver system"
            description={
              isFixed('waivers.system')
                ? 'Set when the league was made; it holds for the season.'
                : undefined
            }
            searchable={false}
            required
            value={d.waivers.system}
            onChange={(v) =>
              v &&
              set((s) => {
                s.waivers.system = v as LeagueSettings['waivers']['system'];
                if (v === 'faab' && !s.waivers.faab)
                  s.waivers.faab = {
                    budget: 100,
                    minBid: 0,
                    tiebreak: 'waiver-priority',
                    allowZeroBids: true,
                  };
              })
            }
            options={[
              { value: 'faab', label: 'Free-agent budget (FAAB)' },
              { value: 'rolling', label: 'Rolling priority' },
              { value: 'reverse-standings', label: 'Reverse standings' },
              { value: 'none', label: 'None: first come, first served' },
            ]}
            disabled={disabled || isFixed('waivers.system')}
          />
          {d.waivers.system === 'faab' && d.waivers.faab ? (
            <div className="fsp-form-pair">
              <NumberField
                label="Season budget ($)"
                value={d.waivers.faab.budget}
                onChange={(v) => set((s) => (s.waivers.faab!.budget = v ?? 0))}
                min={0}
                disabled={disabled || isFixed('waivers.faab')}
              />
              <NumberField
                label="Minimum bid ($)"
                value={d.waivers.faab.minBid}
                onChange={(v) => set((s) => (s.waivers.faab!.minBid = v ?? 0))}
                min={0}
                disabled={disabled || isFixed('waivers.faab')}
              />
            </div>
          ) : null}
          {d.waivers.system !== 'none' ? (
            <div className="fsp-form-triple">
              <Choice
                label="Claims process"
                searchable={false}
                required
                value={d.waivers.processing.schedule}
                onChange={(v) =>
                  v &&
                  set(
                    (s) => (s.waivers.processing.schedule = v as 'daily' | 'weekly' | 'continuous'),
                  )
                }
                options={[
                  { value: 'weekly', label: 'Weekly' },
                  { value: 'daily', label: 'Daily' },
                  { value: 'continuous', label: 'As claims clear' },
                ]}
                disabled={disabled}
              />
              <Choice
                label="Day"
                searchable={false}
                required
                value={String(d.waivers.processing.weekday ?? 3)}
                onChange={(v) => v && set((s) => (s.waivers.processing.weekday = Number(v)))}
                options={waiverDayOptions(sport, [
                  d.waivers.processing.weekday ?? 3,
                  savedWaiverDay,
                ])}
                disabled={disabled || d.waivers.processing.schedule !== 'weekly'}
              />
              <TextField
                type="time"
                label="Time"
                value={d.waivers.processing.time}
                onChange={(v) => set((s) => (s.waivers.processing.time = v))}
                disabled={disabled || d.waivers.processing.schedule === 'continuous'}
              />
            </div>
          ) : null}
          <NumberField
            label="Dropped players sit on waivers (hours)"
            value={d.waivers.claimPeriodHours}
            onChange={(v) => set((s) => (s.waivers.claimPeriodHours = v ?? 0))}
            min={0}
            disabled={disabled}
          />
          <Stack gap={1}>
            <Toggle
              label="Game-time lock"
              on={d.waivers.gameTimeLock}
              onChange={(on) => set((s) => (s.waivers.gameTimeLock = on))}
              disabled={disabled}
            />
            <Text tone="secondary" size="xs" block>
              {gameTimeLockSentences(d.waivers).join(' ')}
            </Text>
          </Stack>
          <Caption>Pickup limits</Caption>
          <div className="fsp-form-pair">
            <NumberField
              label="Per week"
              description="Blank for no limit"
              value={d.acquisitions.perPeriod}
              onChange={(v) => set((s) => (s.acquisitions.perPeriod = v))}
              min={0}
              nullable
              disabled={disabled}
            />
            <NumberField
              label="Per season"
              description="Blank for no limit"
              value={d.acquisitions.perSeason}
              onChange={(v) => set((s) => (s.acquisitions.perSeason = v))}
              min={0}
              nullable
              disabled={disabled}
            />
          </div>
        </div>
      );
    case 'trades':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="Trade review"
            searchable={false}
            required
            value={d.trades.review}
            onChange={(v) =>
              v && set((s) => (s.trades.review = v as LeagueSettings['trades']['review']))
            }
            options={[
              { value: 'none', label: 'None: trades go through' },
              { value: 'commissioner', label: 'Commissioner reviews' },
              { value: 'league-vote', label: 'League can veto' },
            ]}
            disabled={disabled}
          />
          {d.trades.review !== 'none' ? (
            <NumberField
              label="Review window (hours)"
              value={d.trades.reviewWindowHours}
              onChange={(v) => set((s) => (s.trades.reviewWindowHours = v ?? 0))}
              min={0}
              disabled={disabled}
            />
          ) : null}
          <Choice
            label="Trade deadline"
            searchable={false}
            required
            value={d.trades.deadlinePeriod === null ? 'none' : String(d.trades.deadlinePeriod)}
            onChange={(v) =>
              set((s) => (s.trades.deadlinePeriod = !v || v === 'none' ? null : Number(v)))
            }
            options={[{ value: 'none', label: 'No deadline' }, ...periodOptions]}
            disabled={disabled}
          />
          <NumberField
            label="Players per side, at most"
            value={d.trades.maxPlayersPerSide}
            onChange={(v) => set((s) => (s.trades.maxPlayersPerSide = v ?? 1))}
            min={1}
            max={10}
            disabled={disabled}
          />
          {/* TODO(G3): a trade carries players and draft picks; nothing trades budget yet. */}
          <Toggle
            label="Draft picks can be traded"
            description="The coming draft's picks, until it starts, and in a keeper or dynasty league the seasons ahead the Keepers rules say."
            on={d.trades.assets.draftPicks}
            onChange={(on) => set((s) => (s.trades.assets.draftPicks = on))}
            disabled={disabled}
          />
          <Toggle
            label="Free-agent budget can be traded"
            on={d.trades.assets.faab}
            onChange={(on) => set((s) => (s.trades.assets.faab = on))}
            disabled={disabled}
          />
          <Toggle
            label="Trades allowed during the playoffs"
            on={d.trades.allowDuringPlayoffs}
            onChange={(on) => set((s) => (s.trades.allowDuringPlayoffs = on))}
            disabled={disabled}
          />
        </div>
      );
    case 'draft':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="How the league drafts"
            searchable={false}
            required
            value={d.draft.mode}
            onChange={(v) => v && set((s) => (s.draft.mode = v as LeagueSettings['draft']['mode']))}
            options={[
              { value: 'autopick', label: 'Autopick: the site drafts at the time' },
              {
                value: 'manual',
                label: 'Offline: drafted in person, entered by the commissioner',
              },
              { value: 'live', label: 'Live: everyone drafts together in the draft room' },
            ]}
            disabled={disabled}
          />
          <Choice
            label="Draft type"
            searchable={false}
            required
            value={d.draft.format === 'auction' ? 'auction' : (d.draft.order ?? 'snake')}
            onChange={(v) =>
              v &&
              set((s) => {
                s.draft.format = 'pick';
                s.draft.order = v as NonNullable<LeagueSettings['draft']['order']>;
              })
            }
            options={[
              { value: 'snake', label: 'Snake' },
              { value: 'linear', label: 'Linear' },
              { value: 'third-round-reversal', label: 'Third-round reversal' },
              // AN AUCTION IS CHOSEN WHEN A LEAGUE IS MADE (B-197), with its budget
              // and clocks; the practice league's simulator drafts a snake, so it is
              // never switched to here.
              // TODO(B3): a pick draft made an auction before the draft, or back,
              // with the auction's budget and clocks in this section.
              { value: 'auction', label: 'Auction', disabled: true },
            ]}
            disabled={disabled}
          />
          <TextField
            type="datetime-local"
            label="Date and time"
            value={toLocalInput(d.draft.scheduledAt)}
            onChange={(v) =>
              set((s) => (s.draft.scheduledAt = v ? new Date(v).toISOString() : null))
            }
            disabled={disabled}
          />
          {/* An offline draft's order is the one the league drafted in, which
              the commissioner enters with the picks (B-267). */}
          {d.draft.mode !== 'manual' ? (
            <Choice
              label="Draft order"
              searchable={false}
              required
              value={d.draft.seatOrder}
              onChange={(v) =>
                v && set((s) => (s.draft.seatOrder = v as LeagueSettings['draft']['seatOrder']))
              }
              options={[
                { value: 'random', label: 'Random, revealed before the draft' },
                // TWO ORDERS NOTHING HONORS YET (the audit of 2026-10-20), so
                // they are shown and not chosen. Chosen, the first failed the
                // save with nothing on this screen to set the order by, and the
                // second drew at random.
                // TODO(B5): the commissioner's own order, set here before the draft.
                { value: 'manual', label: 'Set by the commissioner', disabled: true },
                // LAST SEASON'S FINISH, REVERSED (H6, B-530): offered once there
                // is a last season to reverse.
                {
                  value: 'reverse-standings',
                  label: 'Reverse of last season’s finish',
                  disabled: !hasLastSeason,
                },
              ]}
              disabled={disabled}
            />
          ) : null}
          {/* Each mode's own rules (B-267): a ranking is the autopick
              draft's; a pick clock and what it does are the live room's. */}
          {d.draft.mode === 'autopick' ? (
            <Choice
              label="How managers rank players"
              searchable={false}
              required
              value={d.draft.ranking}
              onChange={(v) =>
                v && set((s) => (s.draft.ranking = v as LeagueSettings['draft']['ranking']))
              }
              options={[
                { value: 'overall', label: 'One list, with position limits' },
                { value: 'by-position', label: 'By position, with a position each round' },
              ]}
              disabled={disabled}
            />
          ) : null}
          <div className="fsp-form-pair">
            <NumberField
              label="Rounds"
              value={d.draft.rounds}
              onChange={(v) => set((s) => (s.draft.rounds = v ?? 1))}
              min={1}
              max={40}
              disabled={disabled}
            />
            {d.draft.mode === 'live' ? (
              // THE CLOCKS THE BIG SITES OFFER, seconds for an evening and hours
              // for a slow draft (C7), where a number of seconds made an
              // eight-hour clock a sum: 28800.
              <Choice
                label="Time for each pick"
                searchable={false}
                required
                value={String(d.draft.pickSeconds)}
                options={pickClockOptions(d.draft.pickSeconds)}
                onChange={(v) => v && set((s) => (s.draft.pickSeconds = Number(v)))}
                disabled={disabled}
              />
            ) : null}
          </div>
          {d.draft.mode === 'live' ? (
            <>
              <Choice
                label="When the clock runs out"
                searchable={false}
                required
                value={d.draft.whenTheClockExpires}
                onChange={(v) =>
                  v &&
                  set(
                    (s) =>
                      (s.draft.whenTheClockExpires =
                        v as LeagueSettings['draft']['whenTheClockExpires']),
                  )
                }
                options={[
                  {
                    value: 'queue-then-rankings',
                    label: 'The room picks: the queue, then priorities, then the board',
                  },
                  { value: 'rankings', label: 'The room picks: priorities, then the board' },
                  { value: 'off', label: 'Nothing: the room waits for the pick' },
                ]}
                disabled={disabled}
              />
              {/* THE OVERNIGHT PAUSE (C7, B-341), for a slow draft: the clock
                  stops in the league's own hours, and a pick made then still
                  counts. Shown for a clock of an hour or more and whenever one
                  is set; in a real league it is fixed with the rest of the
                  draft, asked when the league is made (B-197). */}
              {/* A REAL LEAGUE'S ONLY: the practice league's room keeps no
                  overnight stop, and a control here would promise one. */}
              {source === 'api' &&
              (isSlowClock(d.draft.pickSeconds) || d.draft.overnightPause !== null) ? (
                <>
                  <Toggle
                    label="Stop the clock overnight"
                    on={d.draft.overnightPause !== null}
                    onChange={(on) =>
                      set(
                        (s) =>
                          (s.draft.overnightPause = on ? { from: '00:00', to: '08:00' } : null),
                      )
                    }
                    disabled={disabled}
                  />
                  {d.draft.overnightPause !== null ? (
                    <Line gap={3} wrap>
                      <Choice
                        label="From"
                        searchable={false}
                        required
                        options={HALF_HOURS.filter((t) => t.value !== d.draft.overnightPause?.to)}
                        value={d.draft.overnightPause.from}
                        onChange={(v) =>
                          v &&
                          set((s) => {
                            if (s.draft.overnightPause) s.draft.overnightPause.from = v;
                          })
                        }
                        disabled={disabled}
                      />
                      <Choice
                        label="Until"
                        searchable={false}
                        required
                        options={HALF_HOURS.filter((t) => t.value !== d.draft.overnightPause?.from)}
                        value={d.draft.overnightPause.to}
                        onChange={(v) =>
                          v &&
                          set((s) => {
                            if (s.draft.overnightPause) s.draft.overnightPause.to = v;
                          })
                        }
                        disabled={disabled}
                      />
                    </Line>
                  ) : null}
                  <Text size="xs" tone="secondary" block>
                    In the league’s time. A pick made while the clock is stopped still counts, and
                    no clock runs out then.
                  </Text>
                </>
              ) : null}
              <Toggle
                label="Trades during the draft"
                on={d.draft.pickTradingAllowed}
                onChange={(on) => set((s) => (s.draft.pickTradingAllowed = on))}
                disabled={disabled}
              />
            </>
          ) : null}
        </div>
      );
    case 'keepers':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="Keepers"
            searchable={false}
            required
            value={d.keepers.mode}
            onChange={(v) =>
              v && set((s) => (s.keepers.mode = v as LeagueSettings['keepers']['mode']))
            }
            options={[
              { value: 'redraft', label: 'Redraft: fresh every season' },
              { value: 'keeper', label: 'Keeper league' },
              { value: 'dynasty', label: 'Dynasty' },
            ]}
            // A SEASON STARTED FROM THE LAST keeps the format it was started
            // with (PLAN-2026-10-27-the-next-season §2.1 rule 5).
            description={
              isFixed('keepers.mode')
                ? 'This season’s format was chosen when it was started from the last one.'
                : undefined
            }
            disabled={disabled || isFixed('keepers.mode')}
          />
          {/* A KEEPER LEAGUE'S RULES (PLAN-2026-10-27-the-next-season §2.3):
              how many, what each costs in the draft's own terms, and for how
              long. A dynasty keeps every roster, so it has no count or cost. */}
          {d.keepers.mode === 'keeper' ? (
            <>
              <NumberField
                label="Players each team keeps"
                value={d.keepers.keeperCount}
                onChange={(v) => set((s) => (s.keepers.keeperCount = v ?? 0))}
                min={0}
                max={d.draft.rounds}
                disabled={disabled}
              />
              <Choice
                label="A keeper costs"
                searchable={false}
                required
                value={d.keepers.cost}
                onChange={(v) =>
                  v && set((s) => (s.keepers.cost = v as LeagueSettings['keepers']['cost']))
                }
                options={
                  d.draft.format === 'auction'
                    ? [
                        { value: 'none', label: 'Nothing from the budget' },
                        { value: 'auction-value', label: 'What he went for last season' },
                      ]
                    : [
                        { value: 'none', label: 'One of the team’s earliest picks' },
                        { value: 'draft-round', label: 'The round he was drafted in' },
                        {
                          value: 'draft-round-escalating',
                          label: 'The round he was drafted in, earlier each year',
                        },
                      ]
                }
                disabled={disabled}
              />
              {d.keepers.cost === 'draft-round-escalating' ? (
                <NumberField
                  label="Rounds earlier each year he is kept"
                  value={d.keepers.escalationRounds}
                  onChange={(v) => set((s) => (s.keepers.escalationRounds = v ?? 1))}
                  min={1}
                  max={5}
                  disabled={disabled}
                />
              ) : null}
              <NumberField
                label="Seasons in a row a player can be kept"
                description="Blank for no limit"
                value={d.keepers.maxYearsKept}
                onChange={(v) => set((s) => (s.keepers.maxYearsKept = v))}
                min={1}
                nullable
                disabled={disabled}
              />
            </>
          ) : null}
          {d.keepers.mode === 'dynasty' ? (
            // A DYNASTY'S DRAFT AFTER ITS FIRST SEASON (PLAN-2026-10-27-the-
            // next-season §2.4 rule 2): rookies only, or anyone on no team.
            <Toggle
              label="Rookie draft each season"
              description={
                d.keepers.rookieDraft
                  ? 'After the first season the draft takes only rookies.'
                  : 'After the first season the draft takes any player on no team.'
              }
              on={d.keepers.rookieDraft}
              onChange={(on) => set((s) => (s.keepers.rookieDraft = on))}
              disabled={disabled}
            />
          ) : null}
          {d.keepers.mode !== 'redraft' ? (
            // PICKS AHEAD (PLAN-2026-10-27-the-next-season §2.6 rule 2):
            // Sleeper's up to three seasons, traded where picks are.
            <NumberField
              label="Picks traded this many seasons ahead"
              description={
                d.trades.assets.draftPicks
                  ? 'Besides the coming draft’s. 0 trades only those.'
                  : 'Draft picks are not traded: Trades has the switch.'
              }
              value={d.keepers.futurePickTrading}
              onChange={(v) => set((s) => (s.keepers.futurePickTrading = v ?? 0))}
              min={0}
              max={3}
              disabled={disabled}
            />
          ) : null}
        </div>
      );
    case 'governance':
      return (
        <div className="fsp-form-fields">
          <Choice
            label="Rule changes need"
            searchable={false}
            required
            value={d.governance.changesRequire}
            onChange={(v) =>
              v &&
              set(
                (s) =>
                  (s.governance.changesRequire =
                    v as LeagueSettings['governance']['changesRequire']),
              )
            }
            options={[
              { value: 'commissioner', label: 'The commissioner' },
              { value: 'majority-vote', label: 'A yes from a majority of the managers' },
              { value: 'supermajority-vote', label: 'A yes from a supermajority of the managers' },
            ]}
            disabled={disabled}
          />
          <Stack gap={1}>
            <Caption>Locked after the draft</Caption>
            <Chips
              label="Locked after the draft"
              options={LOCKABLE.map((k) => ({ value: k, label: k }))}
              value={d.governance.lockedAfterDraft}
              onChange={(v) =>
                set(
                  (s) =>
                    (s.governance.lockedAfterDraft =
                      v as LeagueSettings['governance']['lockedAfterDraft']),
                )
              }
              disabled={disabled}
            />
          </Stack>
          <Choice
            label="Scoring changes apply"
            searchable={false}
            required
            value={d.governance.scoringChangesApply}
            onChange={(v) =>
              v &&
              set(
                (s) =>
                  (s.governance.scoringChangesApply =
                    v as LeagueSettings['governance']['scoringChangesApply']),
              )
            }
            options={[
              { value: 'future-periods', label: 'From the next week; nothing scored changes' },
              // TODO(B3): re-scoring weeks already played under a new version.
              {
                value: 'whole-season',
                label: 'To the whole season, re-scoring every week (soon)',
                disabled: true,
              },
            ]}
            disabled={disabled}
          />
        </div>
      );
    case 'fun': {
      const a = d.fun.leagueAwards;
      const ach = d.fun.achievements;
      // League awards, one chip each. Account achievements have no list here:
      // most are hidden, so their names never reach a client (ACHIEVEMENTS.md
      // §15), and the league's switch is the whole of what a commissioner sets
      // for them. This list once offered the league milestones under
      // "Achievements tracked" and wrote their ids into
      // `fun.achievements.disabled`, which the account engine reads by
      // achievement id — and two ids are both (`dealmaker`, `playoff-bound`).
      // So a league with anything in that list says how many, without naming
      // them, and can turn them all back on (the review of A4).
      const chips = (title: string, defs: readonly { id: string; name: string }[]) => (
        <Stack gap={1}>
          <Caption>{title}</Caption>
          <Chips
            label={title}
            options={defs.map((x) => ({ value: x.id, label: a.names[x.id] ?? x.name }))}
            value={defs.filter((x) => !a.disabled.includes(x.id)).map((x) => x.id)}
            onChange={(v) =>
              set((s) => {
                const on = new Set(v);
                const ids = defs.map((x) => x.id);
                s.fun.leagueAwards.disabled = [
                  ...s.fun.leagueAwards.disabled.filter((id: string) => !ids.includes(id)),
                  ...ids.filter((id) => !on.has(id)),
                ];
              })
            }
            disabled={disabled}
          />
        </Stack>
      );
      return (
        <Stack gap={3}>
          <Caption>League awards — won in this league, this season</Caption>
          <Toggle
            label="Weekly league awards, handed out when each week is settled"
            on={a.perPeriod}
            onChange={(on) => set((s) => (s.fun.leagueAwards.perPeriod = on))}
            disabled={disabled}
          />
          <Toggle
            label="Season league awards, handed out when the championship is final"
            on={a.perSeason}
            onChange={(on) => set((s) => (s.fun.leagueAwards.perSeason = on))}
            disabled={disabled}
          />
          <Toggle
            label="Post award cards and the weekly recap to chat"
            on={a.postToChat}
            onChange={(on) => set((s) => (s.fun.leagueAwards.postToChat = on))}
            disabled={disabled}
          />
          {chips('Weekly league awards handed out', WEEKLY_AWARDS)}
          {chips('Season league awards handed out', SEASON_AWARDS)}
          <Caption>The weekly hat</Caption>
          <Toggle
            label="The week’s fewest points wear a silly hat the week’s most points pick, until next week’s"
            on={d.fun.hat.enabled}
            onChange={(on) => set((s) => (s.fun.hat.enabled = on))}
            disabled={disabled}
          />
          <Caption>Achievements — earned on your account, across every league</Caption>
          <Toggle
            label="Track achievements, unlocked as they happen"
            on={ach.enabled}
            onChange={(on) => set((s) => (s.fun.achievements.enabled = on))}
            disabled={disabled}
          />
          <Toggle
            label="Post unlocks to chat"
            on={ach.postToChat}
            onChange={(on) => set((s) => (s.fun.achievements.postToChat = on))}
            disabled={disabled || !ach.enabled}
          />
          {ach.disabled.length > 0 ? (
            <Line spread>
              <Text tone="secondary" size="xs">
                {ach.disabled.length === 1
                  ? 'One achievement is turned off in this league.'
                  : `${ach.disabled.length} achievements are turned off in this league.`}
              </Text>
              <Button
                size="xs"
                variant="quiet"
                onClick={() => set((s) => (s.fun.achievements.disabled = []))}
                disabled={disabled}
              >
                Turn them back on
              </Button>
            </Line>
          ) : null}
          <Stack gap={1}>
            <Caption>Custom names</Caption>
            <Text tone="secondary" size="xs" block>
              Rename any award for this league. Leave one blank to keep the default.
            </Text>
            <div className="fsp-form-names">
              {[...WEEKLY_AWARDS, ...SEASON_AWARDS].map((x) => (
                <TextField
                  key={x.id}
                  label={x.name}
                  placeholder={x.name}
                  value={a.names[x.id] ?? ''}
                  maxLength={40}
                  disabled={disabled}
                  onChange={(v) =>
                    set((s) => {
                      const names = { ...s.fun.leagueAwards.names };
                      if (v.trim()) names[x.id] = v;
                      else delete names[x.id];
                      s.fun.leagueAwards.names = names;
                    })
                  }
                />
              ))}
            </div>
          </Stack>
        </Stack>
      );
    }
    case 'tools': {
      // RULES_SCHEMA §15c. The read-out and the total are built from our
      // projections, so they read off and cannot be switched while projections
      // are off; their own switches keep what was set for when they come back.
      const t = d.tools;
      const needs = t.projections
        ? undefined
        : 'Built from projected points, so off while they are.';
      return (
        <Stack gap={3}>
          <Text tone="secondary" size="xs" block>
            Turning a helper off takes its numbers off every screen in this league, for everyone.
            Nobody loses a move they could make, and scores never change.
          </Text>
          <Toggle
            label="Projected points"
            description="Our projections on lineups, players, matchups and trades."
            on={t.projections}
            onChange={(on) => set((s) => (s.tools.projections = on))}
            disabled={disabled}
          />
          <Toggle
            label="Worth a look and trending"
            description="Players worth a look, who is trending, and trade partners."
            on={t.suggestions}
            onChange={(on) => set((s) => (s.tools.suggestions = on))}
            disabled={disabled}
          />
          <Toggle
            label="Trade read-out"
            description={needs ?? 'What a trade does to each lineup, and each side’s worth.'}
            on={t.projections && t.tradeAnalysis}
            onChange={(on) => set((s) => (s.tools.tradeAnalysis = on))}
            disabled={disabled || !t.projections}
          />
          <Toggle
            label="Lineup total"
            description={needs ?? 'The starters’ projected total, which moves as players swap.'}
            on={t.projections && t.sandbox}
            onChange={(on) => set((s) => (s.tools.sandbox = on))}
            disabled={disabled || !t.projections}
          />
        </Stack>
      );
    }
  }
}

/**
 * The days a weekly run may fall on (B-509), with the day being edited and the
 * league's saved day when either predates the rule: the saved one the league
 * keeps until it saves another.
 */
function waiverDayOptions(
  sport: SportDefinition,
  also: readonly (number | null)[],
): ChoiceOption[] {
  const days = waiverWeekdaysOf(sport);
  return WEEKDAYS.flatMap((w, i) =>
    days.includes(i) || also.includes(i) ? [{ value: String(i), label: w }] : [],
  );
}

function swap<T>(arr: T[], i: number, j: number): void {
  if (j < 0 || j >= arr.length) return;
  const t = arr[i]!;
  arr[i] = arr[j]!;
  arr[j] = t;
}
